// Supplier history on the award screens (Lane 1395 part 2; plan section 3, Ryan's go
// 2026-10-06): a small mark next to a supplier's name on Offers, Scenarios and the
// result when our records show late deliveries, prices up faster than the market, or a
// letter in the last 12 months. A click opens what we know. Every sentence and figure
// comes from the server, which reads the product's own screens' functions. Nothing is
// applied on its own: the buttons add an ordinary Supplier limits rule.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IntelFacts } from './NegotiationIntel';
import { PoDeliveryFigures } from '../../components/PoDeliveryChanges';
import useDialogFocus from './useDialogFocus';
import './negotiation.css';

const HistoryContext = createContext(null);
// Marks read at the same time: each takes a second or two, so they load after the page
// and one slow supplier never holds the others back.
const AT_ONCE = 3;
const MARK_WORDS = { delivery: 'Late deliveries', price: 'Prices up faster than the market',
  letters: 'A recent letter' };
const READ_FAILED = { marks: [], not_checked: [{ key: 'all', state: 'unavailable',
  text: 'Our records could not be read just now.' }] };
// The dossier's lead-time format: whole days, "~" because it is an average.
const days = (v) => (v === null || v === undefined ? null : `~${Math.round(Number(v))} days`);

export function SupplierHistoryProvider({ api, eventId, live, refreshKey, currency, canAddRules,
  onRulesChanged, children }) {
  const [marks, setMarks] = useState({});
  const [listFailed, setListFailed] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Suppliers already asked for (or being asked for) in this event: a later refresh
  // (an invitation, a rule) asks only for new suppliers and for ones that failed.
  const asked = useRef(new Set());
  // The event the answers belong to: an answer for an event the page has left is dropped.
  const current = useRef(eventId);
  useEffect(() => {
    current.current = eventId;
    asked.current = new Set();
    setMarks({});
    setListFailed(false);
  }, [eventId]);
  useEffect(() => {
    if (!live) return undefined;
    let alive = true;
    const here = eventId;
    const mine = () => current.current === here;
    api.supplierHistoryList(eventId).then(({ suppliers }) => {
      if (!alive || !mine()) return;
      setListFailed(false);
      const queue = suppliers.map((s) => s.supplier_id).filter((id) => !asked.current.has(id));
      queue.forEach((id) => asked.current.add(id));
      const next = () => {
        if (queue.length === 0 || !mine()) return;
        const id = queue.shift();
        api.supplierHistoryMarks(eventId, id)
          .then((m) => {
            if (!mine()) return;
            // A check that could not be read is asked again on the next refresh.
            if ((m.not_checked || []).some((x) => x.state === 'unavailable')) asked.current.delete(id);
            setMarks((prev) => ({ ...prev, [id]: m }));
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.warn('[SOURCING-SUPPLIER-HISTORY] marks', id, e && e.message);
            if (!mine()) return;
            asked.current.delete(id);   // asked again on the next refresh
            setMarks((prev) => ({ ...prev, [id]: READ_FAILED }));
          })
          .finally(next);
      };
      for (let i = 0; i < AT_ONCE; i += 1) next();
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn('[SOURCING-SUPPLIER-HISTORY] list', e && e.message);
      if (alive) setListFailed(true);   // every name shows a grey mark: never a clean record
    });
    return () => { alive = false; };
  }, [api, eventId, live, refreshKey]);
  const value = useMemo(() => ({
    // A failed list refresh shows on every name, a clean one included (Codex r2 #4).
    markOf: (id) => {
      const m = marks[Number(id)];
      if (!(listFailed && live)) return m;
      return m ? { ...m, not_checked: [...(m.not_checked || []), ...READ_FAILED.not_checked] } : READ_FAILED;
    },
    open: (id) => setOpenId(Number(id)),
    // What an expanded Suppliers row needs to show the same content (Lane 1395 QA).
    panel: { api, eventId, currency: currency || 'USD', canAddRules, onRulesChanged },
  }), [marks, listFailed, live, api, eventId, currency, canAddRules, onRulesChanged]);
  return (
    <HistoryContext.Provider value={value}>
      {children}
      {openId && (
        <SupplierHistoryDialog api={api} eventId={eventId} supplierId={openId} canAddRules={canAddRules}
          currency={currency || 'USD'} onRulesChanged={onRulesChanged} onClose={() => setOpenId(null)} />
      )}
    </HistoryContext.Provider>
  );
}

// The mark itself: nothing when the supplier has no mark (and every check ran); grey
// when a check could not be read, so no mark never reads as a clean record.
export function HistoryMark({ supplierId }) {
  const ctx = useContext(HistoryContext);
  const m = ctx ? ctx.markOf(supplierId) : null;
  if (!m) return null;
  const failed = (m.not_checked || []).some((x) => x.state === 'unavailable' || x.state === 'partial');
  if (m.marks.length === 0 && !failed) return null;
  const words = m.marks.length ? m.marks.map((x) => MARK_WORDS[x.key] || x.key).join('. ')
    : 'Some of our records could not be read';
  return (
    <button type="button" className={`srce-hmark${m.marks.length ? '' : ' srce-hmark--grey'}`}
      title={`${words}. Click for what we know.`} aria-label={`${words}. What we know about this supplier`}
      onClick={(e) => { e.stopPropagation(); ctx.open(supplierId); }}>!</button>
  );
}

function LeadTimes({ data }) {
  if (!data) return null;
  if (data.rows.length === 0) {
    return <div className="srce-mut">{(data.lines || []).join(' ')}</div>;
  }
  return (
    <div>
      {(data.lines || []).map((l) => <p key={l} className="srce-mut">{l}</p>)}
      <table className="srce-sc-tbl">
        <thead><tr><th>Part</th><th className="srce-num">Quoted</th>
          <th className="srce-num">Our last {data.window_months || 12} months</th><th className="srce-num">Orders</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.part}>
              <td className="srce-mono">{r.part}</td>
              <td className="srce-num">{r.quoted_days === null ? 'Not given' : `${r.quoted_days} days`}</td>
              <td className="srce-num">{data.state !== 'ok' ? 'Not available' : days(r.actual_days) || 'No orders'}</td>
              <td className="srce-num">{r.orders.toLocaleString('en-US')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.overall && data.overall.actual_days !== null && (
        <p className="srce-mut">All their parts: {days(data.overall.actual_days)} from order to receipt, on {data.overall.orders.toLocaleString('en-US')} orders.</p>
      )}
      {data.source && <div className="neg-src">{data.source}</div>}
    </div>
  );
}

function AddRules({ api, eventId, supplierId, name, currency, onRulesChanged }) {
  const [risk, setRisk] = useState('10');
  const [cap, setCap] = useState('');
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const add = async (key, extra) => {
    setBusy(key);
    setError('');
    try {
      await api.ruleAction(eventId, 'add', { rule_type: 'supplier_limits', supplier_id: supplierId,
        max_amount: null, min_amount: null, no_new_parts: false, ...extra });
      setDone((d) => ({ ...d, [key]: true }));
      if (onRulesChanged) onRulesChanged();
    } catch (e) { setError(e.message); } finally { setBusy(null); }
  };
  const riskOk = /^\d+$/.test(risk.trim()) && Number(risk) >= 1 && Number(risk) <= 100;
  const capOk = /^\d+(\.\d{1,2})?$/.test(cap.trim()) && Number(cap) > 0;
  return (
    <div className="srce-col srce-hrules">
      <span className="srce-strong">Use in the evaluation</span>
      <span className="srce-mut">Each button adds a Supplier limits rule for {name} on the Evaluation rules tab. You can leave it out of any scenario.</span>
      <span className="srce-row">
        <button type="button" className="srce-btn" disabled={Boolean(busy) || done.parts}
          onClick={() => add('parts', { no_new_parts: true })}>{done.parts ? 'Added: no new parts' : 'No new parts for them'}</button>
      </span>
      <span className="srce-row">
        <label className="srce-row">Count a risk cost of
          <input className="srce-input srce-input--num" value={risk} inputMode="numeric" aria-label="Risk cost percent"
            onChange={(e) => setRisk(e.target.value)} disabled={done.risk} />%</label>
        <button type="button" className="srce-btn" disabled={Boolean(busy) || !riskOk || done.risk}
          onClick={() => add('risk', { risk_percent: Number(risk) })}>{done.risk ? 'Added' : 'Add'}</button>
      </span>
      <span className="srce-row">
        <label className="srce-row">Cap their business at
          <input className="srce-input srce-input--num" value={cap} inputMode="decimal" aria-label={`Most a year, ${currency}`}
            onChange={(e) => setCap(e.target.value)} disabled={done.cap} />{currency} a year</label>
        <button type="button" className="srce-btn" disabled={Boolean(busy) || !capOk || done.cap}
          onClick={() => add('cap', { max_amount: cap.trim() })}>{done.cap ? 'Added' : 'Add'}</button>
      </span>
      {error && <div className="srce-error" role="alert">{error}</div>}
    </div>
  );
}

// One supplier's history read: loads, fails with a retry, and reloads when asked.
function useSupplierHistory(api, eventId, supplierId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!api) return undefined;   // outside the workspace's provider: nothing to read
    let alive = true;
    setData(null);
    setError('');
    api.supplierHistory(eventId, supplierId)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [api, eventId, supplierId, tick]);
  return { data, error, retry: () => setTick((t) => t + 1) };
}

// What we know about one supplier: the pop-up's body, also shown in an expanded row of
// the Suppliers tab (Lane 1395 QA, Ryan: "a row that expands in place").
function HistoryContent({ data, error, retry }) {
  return (
    <>
      {error && (
        <div className="srce-error" role="alert">
          {error} <button type="button" className="ll-clr" onClick={retry}>Try again</button>
        </div>
      )}
      {!data && !error && <div className="ll-loading">Reading our records. This takes a few seconds.</div>}
      {data && (
        <>
          <div className="srce-col" data-testid="sh-why">
            {data.marks.length > 0 ? (
              <>
                <span className="srce-strong">Why the mark shows</span>
                {data.marks.map((m) => <p key={m.key} className="srce-hwhy">{m.text}</p>)}
              </>
            ) : (
              <span className="srce-mut">No mark. The mark shows when on time is under 75% (at least 10 receipts with a promise date), prices rose 5 or more points above the market over 12 months, or a letter came in the last 12 months.</span>
            )}
            {data.not_checked.map((m) => <span key={m.key} className="srce-mut">Not checked: {m.text}</span>)}
          </div>
          <IntelFacts sections={data.sections} testPrefix="sh" />
          <div className="neg-facts">
            <div className="neg-fact" data-testid="sh-date-changes">
              <span className="neg-k">Delivery dates moved</span>
              <div>
                {data.date_changes ? <PoDeliveryFigures data={data.date_changes} />
                  : <p className="srce-mut">No delivery date change history is loaded.</p>}
                {data.date_changes && <div className="neg-src">The change log does not say who asked for each move, so it never sets the mark.</div>}
              </div>
            </div>
            <div className="neg-fact" data-testid="sh-lead-times">
              <span className="neg-k">Lead time</span>
              <LeadTimes data={data.lead_times} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

function SupplierHistoryDialog({ api, eventId, supplierId, currency, canAddRules, onRulesChanged, onClose }) {
  const box = useDialogFocus(onClose);
  const h = useSupplierHistory(api, eventId, supplierId);
  const name = h.data ? h.data.supplier_name : 'this supplier';
  return (
    <div className="srce-overlay" role="dialog" aria-modal="true" aria-label={`What we know about ${name}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="srce-modal" ref={box}>
        <div className="srce-card2-h"><span className="srce-card2-t">What we know about {name}</span>
          <span className="srce-pill srce-pill--gray">Your team only</span>
          <button type="button" className="srce-chip-x srce-push" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" /></svg>
          </button></div>
        <div className="srce-modal-b" aria-busy={!h.data && !h.error}>
          <HistoryContent {...h} />
        </div>
        {h.data && canAddRules && (
          <div className="srce-modal-f">
            <AddRules api={api} eventId={eventId} supplierId={supplierId} name={name} currency={currency}
              onRulesChanged={onRulesChanged} />
          </div>
        )}
      </div>
    </div>
  );
}

// The expanded Suppliers row (Lane 1395 QA): the pop-up's content in place, inside the
// workspace's history provider (null outside it).
export function SupplierHistoryPanel({ supplierId }) {
  const ctx = useContext(HistoryContext);
  const p = ctx ? ctx.panel : null;
  const h = useSupplierHistory(p ? p.api : null, p ? p.eventId : null, supplierId);
  if (!p) return null;
  const name = h.data ? h.data.supplier_name : 'this supplier';
  return (
    <div className="srce-hpanel srce-stack" aria-busy={!h.data && !h.error} data-testid="supplier-history-panel">
      <div className="srce-row"><span className="srce-strong">What we know about {name}</span>
        <span className="srce-pill srce-pill--gray">Your team only</span></div>
      <HistoryContent {...h} />
      {h.data && p.canAddRules && (
        <AddRules api={p.api} eventId={p.eventId} supplierId={supplierId} name={name} currency={p.currency}
          onRulesChanged={p.onRulesChanged} />
      )}
    </div>
  );
}
